import React, { useEffect, useMemo, useState } from 'react';
import { FormField } from '@/components/FormField';
import { Select } from '@/components/Select';
import { Card, SectionHeader } from '@/components/Card';
import { Target, Info, AlertTriangle } from 'lucide-react';
import { BaseAdSetForm } from './BaseAdSetForm';
import { AttributionWindowSection, buildAttributionSpec } from './AttributionWindowSection';
import { ODAX_MATRIX, GOAL_LABEL, ConversionLocation } from './odaxMatrix';
import { FbAppPicker, FbPagePicker, FbPixelPicker } from '@/components/facebook/MetaResourcePickers';
import type { MetaApp } from '@/services/meta-resources.service';
import { appOsTargeting } from './appTargeting';

const SALES_LOCATIONS = ODAX_MATRIX.OUTCOME_SALES;

const CONV_EVENTS = [
  'PURCHASE', 'ADD_TO_CART', 'INITIATED_CHECKOUT', 'ADD_PAYMENT_INFO',
  'LEAD', 'COMPLETE_REGISTRATION', 'CONTENT_VIEW', 'SEARCH',
];

const GOAL_HINTS: Record<string, { tone: 'info' | 'warning'; body: string }> = {
  OFFSITE_CONVERSIONS: {
    tone: 'info',
    body: 'Optimises for tracked conversion events on your site (Pixel-based). The default and most accurate sales goal.',
  },
  VALUE: {
    tone: 'warning',
    body: 'Optimises for ROAS — Meta needs a Purchase pixel event firing with a `value` parameter. Without value tracking, this collapses to OFFSITE_CONVERSIONS behaviour.',
  },
  LANDING_PAGE_VIEWS: { tone: 'info', body: 'Cheaper than conversions; counts fully-loaded landing pages only.' },
  LINK_CLICKS:        { tone: 'info', body: 'Cheapest signal but lowest qualified intent.' },
  IMPRESSIONS:        { tone: 'info', body: 'Cheap reach with no behavioural signal.' },
  REACH:              { tone: 'info', body: 'Optimises for unique people reached.' },
  CONVERSATIONS: {
    tone: 'info',
    body: 'Messenger destination — optimises for chats started from the ad.',
  },
};

export const SalesAdSetForm: React.FC = () => {
  const [location, setLocation] = useState<ConversionLocation>('WEBSITE');
  const [optGoal, setOptGoal] = useState<string>('OFFSITE_CONVERSIONS');
  const [pixelId, setPixelId] = useState('');
  const [eventType, setEventType] = useState('PURCHASE');
  const [clickWin, setClickWin] = useState('7');
  const [viewWin, setViewWin] = useState('1');
  const [pageId, setPageId] = useState('');
  const [fbAppId, setFbAppId] = useState('');
  const [pickedApp, setPickedApp] = useState<MetaApp | null>(null);
  const objectStoreUrl =
    pickedApp?.objectStoreUrls?.google_play ??
    pickedApp?.objectStoreUrls?.iphone_app_store ??
    pickedApp?.objectStoreUrls?.amazon_app_store ??
    '';

  const locationEntry = useMemo(
    () => SALES_LOCATIONS.find((l) => l.value === location)!,
    [location],
  );

  useEffect(() => {
    if (!locationEntry.goals.includes(optGoal)) setOptGoal(locationEntry.goals[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const goalOptions = locationEntry.goals.map((g) => ({ code: g, label: GOAL_LABEL[g] ?? g }));

  // Live 2026-09-29: every Sales website goal is refused without the pixel ("performance goal isn't
  // available"), chat destinations need the Page (plus the pixel for conversions), app destinations need the
  // app, and only website conversions accept a view-through window — everything else is (1 day, none).
  const isChat = location === 'MESSENGER' || location === 'WHATSAPP';
  const needsPixel = location === 'WEBSITE' || (isChat && optGoal === 'OFFSITE_CONVERSIONS');
  const needsAppEvent = location === 'APP' && optGoal === 'OFFSITE_CONVERSIONS';
  const allowsLongerAttribution = location === 'WEBSITE' && (optGoal === 'OFFSITE_CONVERSIONS' || optGoal === 'VALUE');

  useEffect(() => {
    if (allowsLongerAttribution) return;
    if (clickWin !== '1') setClickWin('1');
    if (viewWin !== '0') setViewWin('0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowsLongerAttribution]);

  const extraPayload = () => {
    const out: Record<string, unknown> = {};
    if (locationEntry.destinationType) out.destination_type = locationEntry.destinationType;
    const promoted: Record<string, string> = {};
    if (isChat && pageId) promoted.page_id = pageId;
    if (needsPixel && pixelId) {
      promoted.pixel_id = pixelId;
      promoted.custom_event_type = eventType;
    }
    if (location === 'APP' && fbAppId) {
      promoted.application_id = fbAppId;
      if (objectStoreUrl) promoted.object_store_url = objectStoreUrl;
      if (needsAppEvent) promoted.custom_event_type = eventType;
    }
    if (Object.keys(promoted).length > 0) out.promoted_object = promoted;
    out.attribution_spec = buildAttributionSpec(clickWin, viewWin);
    return out;
  };

  const extraTargeting = () => (location === 'APP' ? appOsTargeting(objectStoreUrl) : undefined);

  const validateExtra = () => {
    if (isChat && !pageId) return 'Pick a Facebook Page first';
    if (needsPixel && !pixelId) return 'Pick a Meta Pixel for conversion tracking';
    if (location === 'APP' && !fbAppId) return 'Pick a Facebook App for the App destination';
    return null;
  };

  return (
    <BaseAdSetForm
      optimizationGoals={goalOptions}
      extraPayload={extraPayload}
      extraTargeting={extraTargeting}
      validateExtra={validateExtra}
      onOptimizationGoalChange={setOptGoal}
      expectedObjective="OUTCOME_SALES"
      noticeLocation={location}
    >
      <Card padded className="!mt-5">
        <SectionHeader
          title="Where sales happen"
          icon={<Target size={16} />}
          subtitle="Different sales destinations unlock different optimisation goals on Meta."
        />
        <FormField label="Conversion location" required>
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value as ConversionLocation)}
            className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft"
          >
            {SALES_LOCATIONS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </FormField>
      </Card>

      {GOAL_HINTS[optGoal] && (
        <div
          className={
            'flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs ' +
            (GOAL_HINTS[optGoal].tone === 'warning'
              ? 'border-warning/40 bg-warning-soft'
              : 'border-info/40 bg-info-soft')
          }
        >
          {GOAL_HINTS[optGoal].tone === 'warning'
            ? <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
            : <Info size={14} className="mt-0.5 shrink-0 text-info" />}
          <p className="text-text-secondary leading-relaxed">{GOAL_HINTS[optGoal].body}</p>
        </div>
      )}

      {isChat && (
        <FormField label="Facebook Page" required>
          <FbPagePicker value={pageId} restrictToAuthorized onChange={(id) => setPageId(id)} />
        </FormField>
      )}

      {location === 'APP' && (
        <FormField label="Facebook App" required hintBelow hint="Pick from apps you've registered at developers.facebook.com.">
          <FbAppPicker value={fbAppId} mobileOnly onChange={(id, app) => { setFbAppId(id); setPickedApp(app); }} />
        </FormField>
      )}

      {(needsPixel || needsAppEvent) && (
        <>
          {needsPixel && (
            <FormField label="Meta Pixel" required hintBelow hint="Tracks conversions on your website. Pick an existing pixel or create one.">
              <FbPixelPicker value={pixelId} onChange={(id) => setPixelId(id)} />
            </FormField>
          )}
          <FormField label={needsAppEvent ? 'App event' : 'Conversion event'}>
            <Select value={eventType} onChange={e => setEventType(e.target.value)}>
              {CONV_EVENTS.map(e => <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>)}
            </Select>
          </FormField>
        </>
      )}

      <AttributionWindowSection
        clickWin={clickWin}
        setClickWin={setClickWin}
        viewWin={viewWin}
        setViewWin={setViewWin}
        clickOptions={allowsLongerAttribution ? undefined : ['1']}
        hideViewThrough={!allowsLongerAttribution}
        hint={allowsLongerAttribution
          ? 'How far back Meta credits a click or view to a conversion. Default for Sales: 7 days click + 1 day view.'
          : 'Meta only accepts a 1-day click window for this location and goal.'}
      />
    </BaseAdSetForm>
  );
};
